import { getValueFromPropsPath } from "../nested-props.js";
import type { AsMutableArray, MutableOrImmutableArray, ToMutableOrImmutableArray } from "../array-checks.js";
import type { DefaultIfNever } from "../conditional.js";
import type { NestedPropsPathValue } from "../nested-props.js";

export type UnwrapArrayType<A, TDefault = A> =
  A extends MutableOrImmutableArray<infer T>
    ? T
    : TDefault;

export type MapArrayToProp<A extends MutableOrImmutableArray<any>, P extends PropertyKey, T extends UnwrapArrayType<A, any> = UnwrapArrayType<A, any>> =
  P extends keyof T
    ?
    A extends readonly [ infer H extends T, ...infer R extends readonly T[] ]
      ? readonly [ H[ P ], ...MapArrayToProp<R, P> ] 
      :
    A extends [ infer H extends T, ...infer R extends T[] ]
      ? [ H[ P ], ...MapArrayToProp<R, P> ]
      : 
    A extends readonly []
      ? readonly []
      : []
    : never;

export function mapArrayToProp<
  const A extends MutableOrImmutableArray<any>,
  const P extends keyof T,
  T extends UnwrapArrayType<A, any> = UnwrapArrayType<A, any>
> (
  array: A,
  prop: P
): MapArrayToProp<A, P, T>;
export function mapArrayToProp<
  const A extends MutableOrImmutableArray<any>,
  const P extends PropertyKey,
  T extends UnwrapArrayType<A, any> = UnwrapArrayType<A, any>
> (
  array: A,
  prop: P
): MapArrayToProp<A, P, T>;
export function mapArrayToProp<
  const A extends MutableOrImmutableArray<any>,
  const P extends keyof T | PropertyKey,
  T extends UnwrapArrayType<A, any> = UnwrapArrayType<A, any>
> (
  array: A,
  prop: P
): MapArrayToProp<A, P, T> {
  return array.map((el) => {
    return el[ prop ];
  }) as MapArrayToProp<A, P, T>;
}

type FlatMapArrayToPropHelper<
  ARRAY extends MutableOrImmutableArray<any>,
  PROP extends PropertyKey,
  NESTEDARRAY extends MutableOrImmutableArray<U>,
  RESULT extends U[] = [],
  T extends UnwrapArrayType<ARRAY, []> = UnwrapArrayType<ARRAY, []>,
  U extends UnwrapArrayType<T, unknown> = UnwrapArrayType<T, unknown>
> =
  PROP extends keyof T
    ?
    AsMutableArray<NESTEDARRAY> extends [ infer NESTED_HEAD extends U, ...infer NESTED_REM extends U[] ]
      ? FlatMapArrayToPropHelper<ARRAY, PROP, NESTED_REM, [ ...RESULT, NESTED_HEAD ]>
      :
    AsMutableArray<ARRAY> extends [ infer HEAD extends T, ...infer REM extends T[] ]
      ? FlatMapArrayToPropHelper<REM, PROP, HEAD[ PROP ], RESULT>
      : RESULT
    : never;



export type FlatMapArrayToProp<ARRAY extends MutableOrImmutableArray<any>, PROP extends PropertyKey>
  = FlatMapArrayToPropHelper<ARRAY, PROP, [], []>;

export function flatMapArrayToProp<
  const ARRAY extends MutableOrImmutableArray<any>,
  const PROP extends keyof T,
  T extends UnwrapArrayType<ARRAY, []> = UnwrapArrayType<ARRAY, []>
> (
  array: ARRAY,
  prop: PROP
): FlatMapArrayToProp<ARRAY, PROP>;

export function flatMapArrayToProp<
  const ARRAY extends MutableOrImmutableArray<any>,
  const PROP extends PropertyKey
> (
  array: ARRAY,
  prop: PROP
): FlatMapArrayToProp<ARRAY, PROP>;

export function flatMapArrayToProp<
  const ARRAY extends MutableOrImmutableArray<any>,
  const PROP extends keyof T | PropertyKey,
  T extends UnwrapArrayType<ARRAY, []> = UnwrapArrayType<ARRAY, []>
> (
  array: ARRAY,
  prop: PROP
): FlatMapArrayToProp<ARRAY, PROP> {
  return array.flatMap((el) => {
    return el[prop];
  }) as FlatMapArrayToProp<ARRAY, PROP>;
}




export type MapArrayToNestedProp<
  ARRAY extends MutableOrImmutableArray<any>,
  PATH extends ToMutableOrImmutableArray<[ PropertyKey, ...PropertyKey[] ]>,
  T extends UnwrapArrayType<ARRAY, any> = UnwrapArrayType<ARRAY, any>
> =
  AsMutableArray<ARRAY> extends [ infer HEAD extends T, ...infer REMAINING extends T[] ]
    ? [ NestedPropsPathValue<HEAD, PATH>, ...MapArrayToNestedProp<REMAINING, PATH> ] 
    :
  AsMutableArray<ARRAY> extends [] 
    ? [] 
    : never;


export function mapArrayToNestedProp<
  ARRAY extends any[] | readonly any[],
  PATH extends ToMutableOrImmutableArray<[ keyof T, ...PropertyKey[] ]>,
  T extends UnwrapArrayType<ARRAY, any> = UnwrapArrayType<ARRAY, any>
> (
  array: ARRAY,
  path: PATH
): MapArrayToNestedProp<ARRAY, PATH>;

export function mapArrayToNestedProp<
  ARRAY extends any[] | readonly any[],
  PATH extends ToMutableOrImmutableArray<[ PropertyKey, ...PropertyKey[] ]>,
> (
  array: ARRAY,
  path: PATH
): MapArrayToNestedProp<ARRAY, PATH>;

export function mapArrayToNestedProp<
  ARRAY extends any[] | readonly any[],
  PATH extends ToMutableOrImmutableArray<[ keyof T | PropertyKey, ...PropertyKey[] ]>,
  T extends UnwrapArrayType<ARRAY, any> = UnwrapArrayType<ARRAY, any>
> (
  array: ARRAY,
  path: PATH
): MapArrayToNestedProp<ARRAY, PATH> {
  return array.map((el) => {
    return getValueFromPropsPath(el, path);
  }) as MapArrayToNestedProp<ARRAY, PATH>;
}


// type FlatMapArrayToNestedPropHelper1<
//   ARRAY extends MutableOrImmutableArray<any>,
//   PATH extends ToMutableOrImmutableArray<[ keyof T, ...PropertyKey[] ]>,
//   T extends UnwrapArrayType<ARRAY, any> = UnwrapArrayType<ARRAY, any>
// > =
//   AsMutableArray<ARRAY> extends [ infer HEAD extends T, ...infer REMAINING extends T[] ]
//   ? [ NestedPropsPathValue<HEAD, PATH>, ...MapArrayToNestedProp<REMAINING, PATH> ]
//   : AsMutableArray<ARRAY> extends [] ? [] : never;

type FlatMapArrayToNestedPropHelper<
  ARRAY extends MutableOrImmutableArray<any>,
  PATH extends ToMutableOrImmutableArray<[ PropertyKey, ...PropertyKey[] ]>,
  NESTED_ARRAY extends MutableOrImmutableArray<any> = [],
  RESULT extends any[] = [],
  DEFAULT_IF_NEVER = undefined,
  T extends UnwrapArrayType<ARRAY, DEFAULT_IF_NEVER> = UnwrapArrayType<ARRAY, DEFAULT_IF_NEVER>
> =
  AsMutableArray<NESTED_ARRAY> extends [ infer NESTED_HEAD, ...infer NESTED_REM extends any[] ]
  ? FlatMapArrayToNestedPropHelper<ARRAY, PATH, NESTED_REM, [ ...RESULT, NESTED_HEAD ], DEFAULT_IF_NEVER>
  : (
    AsMutableArray<ARRAY> extends [ infer HEAD extends T, ...infer REM extends T[] ]
    ? (
      DefaultIfNever<NestedPropsPathValue<HEAD, PATH>, DEFAULT_IF_NEVER> extends infer HEAD_RESULT
      ? (
        HEAD_RESULT extends any[] | readonly any[]
        ? FlatMapArrayToNestedPropHelper<REM, PATH, HEAD_RESULT, RESULT, DEFAULT_IF_NEVER>
        : FlatMapArrayToNestedPropHelper<REM, PATH, [ HEAD_RESULT ], RESULT, DEFAULT_IF_NEVER>
      )
      : never
    )
    : RESULT
  );


export type FlatMapArrayToNestedProp<
  ARRAY extends MutableOrImmutableArray<any>,
  PATH extends ToMutableOrImmutableArray<[ PropertyKey, ...PropertyKey[] ]>,
  DEFAULT_IF_NEVER = undefined,
  T extends UnwrapArrayType<ARRAY, DEFAULT_IF_NEVER> = UnwrapArrayType<ARRAY, DEFAULT_IF_NEVER>
> = FlatMapArrayToNestedPropHelper<ARRAY, PATH, [], [], DEFAULT_IF_NEVER, T>;

export function flatMapArrayToNestedProp<
  const ARRAY extends MutableOrImmutableArray<any>,
  const PATH extends ToMutableOrImmutableArray<[ keyof T, ...PropertyKey[] ]>,
  const DEFAULT_IF_NEVER,
  T extends UnwrapArrayType<ARRAY, DEFAULT_IF_NEVER> = UnwrapArrayType<ARRAY, DEFAULT_IF_NEVER>
> (
  array: ARRAY,
  path: PATH,
  defaultIfNever?: DEFAULT_IF_NEVER
): FlatMapArrayToNestedProp<ARRAY, PATH, DEFAULT_IF_NEVER, T>;

export function flatMapArrayToNestedProp<
  const ARRAY extends MutableOrImmutableArray<any>,
  const PATH extends ToMutableOrImmutableArray<[ PropertyKey, ...PropertyKey[] ]>,
  const DEFAULT_IF_NEVER,
  T extends UnwrapArrayType<ARRAY, DEFAULT_IF_NEVER> = UnwrapArrayType<ARRAY, DEFAULT_IF_NEVER>
> (
  array: ARRAY,
  path: PATH,
  defaultIfNever?: DEFAULT_IF_NEVER
): FlatMapArrayToNestedProp<ARRAY, PATH, DEFAULT_IF_NEVER, T>;

export function flatMapArrayToNestedProp<
  const ARRAY extends MutableOrImmutableArray<any>,
  const PATH extends ToMutableOrImmutableArray<[ keyof T | PropertyKey, ...PropertyKey[] ]>,
  const DEFAULT_IF_NEVER,
  T extends UnwrapArrayType<ARRAY, DEFAULT_IF_NEVER> = UnwrapArrayType<ARRAY, DEFAULT_IF_NEVER>
> (
  array: ARRAY,
  path: PATH,
  defaultIfNever?: DEFAULT_IF_NEVER
): FlatMapArrayToNestedProp<ARRAY, PATH, DEFAULT_IF_NEVER, T> {
  return array.flatMap((el) => {
    return getValueFromPropsPath(el, path, defaultIfNever);
  }) as FlatMapArrayToNestedProp<ARRAY, PATH, DEFAULT_IF_NEVER, T>;
}